import React, { useState, useCallback } from 'react';

const ONBOARDING_KEY = 'oplixi_onboarding_complete';

const slides = [
  {
    title: 'Welcome to Oplixi',
    description: 'Stream movies free — no signup required. Just open and watch.',
    icon: (
      <svg className="w-16 h-16 text-netflix-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
  },
  {
    title: 'Browse & Discover',
    description: 'New releases, Oplixi Originals, and movies across curated categories — all in one place.',
    icon: (
      <svg className="w-16 h-16 text-netflix-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
  },
  {
    title: 'Made for You',
    description: 'Pick up where you left off, build My List, get personal recommendations, and rate what you watch.',
    icon: (
      <svg className="w-16 h-16 text-netflix-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    ),
  },
  {
    title: 'Never Miss a Premiere',
    description: 'Turn on New Release Alerts and your progress follows you across every device.',
    icon: (
      <svg className="w-16 h-16 text-netflix-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
  },
];

export function hasSeenOnboarding() {
  try {
    return localStorage.getItem(ONBOARDING_KEY) === 'true';
  } catch {
    return false;
  }
}

function markOnboardingComplete() {
  try {
    localStorage.setItem(ONBOARDING_KEY, 'true');
  } catch {
    // localStorage not available
  }
}

function Onboarding({ onComplete }) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const handleFinish = useCallback(() => {
    markOnboardingComplete();
    onComplete();
  }, [onComplete]);

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(prev => prev + 1);
    } else {
      handleFinish();
    }
  };

  const slide = slides[currentSlide];
  const isLast = currentSlide === slides.length - 1;

  return (
    <div className="fixed inset-0 bg-black z-[9999] flex flex-col items-center justify-between py-12 px-6">
      {/* Skip button */}
      {!isLast && (
        <button
          onClick={handleFinish}
          className="absolute top-6 right-6 text-gray-400 hover:text-white text-sm font-medium transition-colors bg-transparent border-none cursor-pointer"
        >
          Skip
        </button>
      )}

      {/* Slide content */}
      <div className="flex-1 flex flex-col items-center justify-center text-center max-w-sm">
        <div className="w-24 h-24 rounded-full bg-white/5 flex items-center justify-center mb-8">
          {slide.icon}
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">{slide.title}</h1>
        <p className="text-gray-400 text-base leading-relaxed">{slide.description}</p>
      </div>

      {/* Navigation */}
      <div className="w-full max-w-sm space-y-6">
        {/* Dots */}
        <div className="flex justify-center gap-2">
          {slides.map((_, i) => (
            <div
              key={i}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === currentSlide ? 'w-6 bg-netflix-red' : 'w-2 bg-white/20'
              }`}
            />
          ))}
        </div>

        {/* Button */}
        <button
          onClick={handleNext}
          className="w-full py-3.5 rounded-lg font-semibold text-sm bg-netflix-red hover:bg-red-700 active:scale-95 text-white transition-all duration-150 border-none cursor-pointer"
        >
          {isLast ? 'Get Started' : 'Next'}
        </button>
      </div>
    </div>
  );
}

export default Onboarding;
